/**
 * useDeviceModelThumbnail — the reactive door to `utils/deviceModelThumbnail`
 * for a card face.
 *
 * The card asks with the DEVICE's own slot (`settings.model3d`, read through the
 * one parser) and the live level a tank's water line follows. The picture is
 * drawn off the render path and cached by the slot's key, so a tile mount is a
 * map lookup once the model has been drawn for its level.
 *
 * The level is debounced and quantised by the key itself (2 % steps): a tank
 * whose reading creeps up redraws only when the water line would actually move
 * a visible pixel.
 */

import { onScopeDispose, ref, watch, type Ref } from 'vue';

import type { DeviceModel3d } from '@/utils/bimDeviceModel';
import { deviceModelThumbnail, deviceModelThumbnailKey } from '@/utils/deviceModelThumbnail';

const REDRAW_DELAY_MS = 250;

export function useDeviceModelThumbnail(
  model: Ref<DeviceModel3d | null>,
  levelPct: Ref<number | null>,
): Ref<string | null> {
  const picture = ref<string | null>(null);
  let token = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function load(): Promise<void> {
    const current = model.value;
    if (!current || deviceModelThumbnailKey(current, levelPct.value) === null) {
      picture.value = null;
      return;
    }
    const mine = (token += 1);
    let drawn: string | null = null;
    try {
      drawn = await deviceModelThumbnail(current, levelPct.value);
    } catch {
      drawn = null;
    }
    // A later model/level change owns the picture — this answer is stale.
    if (mine === token) picture.value = drawn;
  }

  watch(
    () => deviceModelThumbnailKey(model.value, levelPct.value),
    (key) => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (key === null) {
        // No model on this device (or a family the preview cannot build): there
        // is nothing to draw, and a dashboard of plain sensors schedules no work.
        token += 1;
        picture.value = null;
        return;
      }
      timer = setTimeout(() => {
        timer = null;
        void load();
      }, REDRAW_DELAY_MS);
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    token += 1;
    if (timer) clearTimeout(timer);
  });

  return picture;
}
